/** Representation of a single event listener. */
class WrappedListener {
    listener;
    context;
    once;
    constructor(listener, context, once = false) {
        this.listener = listener;
        this.context = context;
        this.once = once;
    }
}
/** Minimal `EventEmitter` interface that is molded against the Node.js `EventEmitter` interface. */
export class EventEmitter {
    static prefixed = false;
    /**
     * Use Object.create(null) to create a truly empty object for storing events, avoiding potential
     * prototype pollution or interference from built-in Object properties.
     */
    _events = Object.create(null);
    _eventsCount = 0;
    /** Add a listener for a given event. */
    #addListener(event, listener, context, once) {
        if (typeof listener !== 'function') {
            throw new TypeError('The listener must be a function');
        }
        const wrappedListener = new WrappedListener(listener, context || this, once);
        const events = this._events[event];
        if (Array.isArray(events)) {
            /** Third or more listener for this event (pushing to existing array) */
            events.push(wrappedListener);
        }
        else if (events) {
            /** Second listener for this event (converting single EE to array) */
            this._events[event] = [
                events,
                wrappedListener,
            ];
        }
        else {
            /** First listener for this event */
            this._events[event] = wrappedListener;
            this._eventsCount++;
        }
        return this;
    }
    /** Clear event by name. */
    clearEvent(event) {
        if (--this._eventsCount === 0) {
            this._events = Object.create(null); // Re-initialize
        }
        else {
            delete this._events[event];
        }
    }
    /** Return an array listing the events for which the emitter has registered listeners. */
    eventNames() {
        if (this._eventsCount === 0) {
            return [];
        }
        /**
         * Use `Reflect.ownKeys` to get both string and symbol properties directly from the _events
         * object.
         */
        return Reflect.ownKeys(this._events);
    }
    /** Return the listeners registered for a given event. */
    listeners(event) {
        const handlers = this._events[event];
        if (!handlers) {
            return [];
        }
        else if (Array.isArray(handlers)) {
            /** Array of listeners. */
            return handlers.map((handler) => handler.listener);
        }
        else {
            /** Single listener. */
            return [
                handlers.listener,
            ];
        }
    }
    /** Return the number of listeners listening to a given event. */
    listenerCount(event) {
        const listeners = this._events[event];
        if (!listeners) {
            return 0;
        }
        else if (Array.isArray(listeners)) {
            /** Array of listeners. */
            return listeners.length;
        }
        else {
            /** Single listener. */
            return 1;
        }
    }
    /** Calls each of the listeners registered for a given event. */
    emit(event, ...args) {
        const listeners = this._events[event];
        if (!listeners) {
            return false;
        }
        if (Array.isArray(listeners)) {
            /** Multiple listeners (array) */
            /** Create a copy to avoid issues if listeners are removed during iteration. */
            const currentListeners = listeners.slice(0);
            currentListeners.forEach((listener) => {
                if (listener.once) {
                    this.removeListener(event, listener.listener, undefined, true);
                }
                listener.listener.call(listener.context, ...args);
            });
        }
        else {
            // Single listener
            if (listeners.once) {
                this.removeListener(event, listeners.listener, undefined, true);
            }
            listeners.listener.call(listeners.context, ...args);
        }
        return true;
    }
    /** Add a listener for a given event. */
    on(event, listener, context) {
        return this.#addListener(event, listener, context, false);
    }
    /** Add a one-time listener for a given event. */
    once(event, listener, context) {
        return this.#addListener(event, listener, context, true);
    }
    /** Remove the listeners of a given event. */
    removeListener(event, listener, context, once) {
        const wrappedListeners = this._events[event];
        if (!wrappedListeners) {
            return this;
        }
        else if (!listener) {
            this.clearEvent(event);
            return this;
        }
        else if (Array.isArray(wrappedListeners)) {
            const events = [];
            wrappedListeners.forEach((wrappedListener) => {
                if (wrappedListener.listener !== listener ||
                    (once && !wrappedListener.once) ||
                    (context && wrappedListener.context !== context)) {
                    events.push(wrappedListener);
                }
            });
            if (events.length) {
                this._events[event] = events.length === 1 ? events[0] : events;
            }
            else {
                this.clearEvent(event);
            }
        }
        else if (wrappedListeners.listener === listener &&
            (!once || wrappedListeners.once) &&
            (!context || wrappedListeners.context === context)) {
            this.clearEvent(event);
        }
        return this;
    }
    /** Remove all listeners, or those of the specified event. */
    removeAllListeners(event) {
        if (event) {
            if (this._events[event]) {
                this.clearEvent(event);
            }
        }
        else {
            this._events = Object.create(null);
            this._eventsCount = 0;
        }
        return this;
    }
    /** Remove the listeners of a given event. */
    off(event, listener, context, once) {
        return this.removeListener(event, listener, context, once);
    }
    /** Add a listener for a given event. */
    addListener(event, listener, context) {
        return this.on(event, listener, context);
    }
}
export default EventEmitter;
